import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { OutletProvider } from "@/lib/outlet";
import ActionDeck from "./procurement/ActionDeck";
import ExceptionQueue from "./procurement/ExceptionQueue";
import IndentsTab from "./procurement/IndentsTab";
import VendorsTab from "./procurement/VendorsTab";
import ItemsTab from "./procurement/ItemsTab";
import POsTab from "./procurement/POsTab";
import GRNsTab from "./procurement/GRNsTab";
import PayoutsTab from "./procurement/PayoutsTab";
import BenchmarksTab from "./procurement/BenchmarksTab";
import SettingsTab from "./procurement/SettingsTab";
import VendorLedger from "./procurement/VendorLedger";
import PendingYouBanner from "./procurement/PendingYouBanner";
import OutletSwitcher from "./procurement/OutletSwitcher";
import { LayoutGrid, ClipboardList, Store, Package, FileText, Truck, Wallet, TrendingDown, Settings2, AlertOctagon } from "lucide-react";

// New editorial ordering — Action Deck first, then daily flow, then master data.
const TABS = [
  { key: "action_deck", label: "Action Deck",   icon: LayoutGrid },
  { key: "exceptions",  label: "Exceptions",    icon: AlertOctagon },
  { key: "indents",     label: "Indents",       icon: ClipboardList },
  { key: "pos",         label: "Purchase Orders", icon: FileText },
  { key: "grns",        label: "GRNs",          icon: Truck },
  { key: "payouts",     label: "Payouts",       icon: Wallet },
  { key: "items",       label: "Item Master",   icon: Package },
  { key: "vendors",     label: "Vendors",       icon: Store },
  { key: "benchmarks",  label: "Benchmarks",    icon: TrendingDown },
  { key: "settings",    label: "Settings",      icon: Settings2 },
];

export default function Procurement() {
  return (
    <OutletProvider>
      <ProcurementInner />
    </OutletProvider>
  );
}

function ProcurementInner() {
  const { user } = useAuth();
  const [tab, setTab] = useState("action_deck");
  const [ledgerVendorId, setLedgerVendorId] = useState(null);
  const [presetFilter, setPresetFilter] = useState(null);

  const jumpTo = (nextTab, filter) => {
    setTab(nextTab);
    if (filter) setPresetFilter({ tab: nextTab, filter });
  };

  if (ledgerVendorId) {
    return (
      <div data-testid="procurement-page">
        <VendorLedger vendorId={ledgerVendorId} onBack={() => setLedgerVendorId(null)} />
      </div>
    );
  }

  return (
    <div data-testid="procurement-page">
      {/* Editorial header — with outlet switcher */}
      <div className="border-y border-[hsl(var(--rule))] py-3 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-3 text-[10px] uppercase tracking-[0.25em] text-[hsl(var(--ink-soft))]">
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-semibold text-[hsl(var(--brass-2))]">EEPL Procurement</span>
            <span aria-hidden>·</span>
            <span>Sourced</span>
            <span aria-hidden>·</span>
            <span>Cultivated</span>
            <span aria-hidden>·</span>
            <span>Served</span>
          </div>
          <div className="flex items-center gap-3">
            <OutletSwitcher />
            <div
              data-testid="procurement-mode-pill"
              className={`px-2.5 py-1 rounded-full border ${
                user?.role === "admin"
                  ? "border-[hsl(var(--forest))] text-[hsl(var(--forest))]"
                  : "border-[hsl(var(--rule))] text-[hsl(var(--ink-soft))]"
              }`}
            >
              Admin mode · {user?.role === "admin" ? "ON" : "OFF"}
            </div>
          </div>
        </div>
      </div>

      <div className="eyebrow">Procurement</div>
      <h1 className="mt-2 display text-4xl lg:text-5xl">Procurement &amp; Vendor Console</h1>
      <p className="mt-3 text-sm text-[hsl(var(--ink-soft))] max-w-2xl leading-relaxed">
        Purchase orders, goods received notes, payouts, indents, item master and rate benchmarking — every touchpoint from vendor to plate, ledgered.
      </p>

      <PendingYouBanner onJumpTo={jumpTo} />

      {/* Numbered editorial tabs */}
      <nav data-testid="procurement-tabs" className="mt-8 border-b border-[hsl(var(--rule))] overflow-x-auto">
        <div className="flex flex-nowrap items-end gap-x-6 gap-y-2 min-w-max pb-1">
          {TABS.map((t, i) => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                data-testid={`procurement-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`group flex items-baseline gap-2 pb-3 border-b-2 -mb-px transition-colors whitespace-nowrap ${
                  active
                    ? "border-[hsl(var(--forest))] text-[hsl(var(--forest))]"
                    : "border-transparent text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--ink))]"
                }`}
              >
                <span className={`font-mono text-[10px] tracking-widest ${active ? "text-[hsl(var(--brass-2))]" : "text-[hsl(var(--ink-soft))]/70"}`}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-xs uppercase tracking-[0.15em]">{t.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      <div className="mt-8">
        {tab === "action_deck" && <ActionDeck onJumpTo={jumpTo} />}
        {tab === "exceptions" && <ExceptionQueue />}
        {tab === "indents" && <IndentsTab />}
        {tab === "pos" && <POsTab presetFilter={presetFilter?.tab === "pos" ? presetFilter.filter : null} />}
        {tab === "grns" && <GRNsTab presetFilter={presetFilter?.tab === "grns" ? presetFilter.filter : null} />}
        {tab === "payouts" && <PayoutsTab onViewLedger={setLedgerVendorId} />}
        {tab === "items" && <ItemsTab />}
        {tab === "vendors" && <VendorsTab onViewLedger={setLedgerVendorId} />}
        {tab === "benchmarks" && <BenchmarksTab />}
        {tab === "settings" && <SettingsTab />}
      </div>
    </div>
  );
}
